import { computed } from 'vue'
import { useStore } from 'vuex'
import {
  type RouteUpSellingDetail,
  NON_OWNER_UPSELLING_MESSAGE,
} from '@/utils/upselling'
import { useRouter } from 'vue-router'
import { DETAILS_OVERRIDES } from '@/utils/upselling'
import { ROUTE_UPSELLING_DETAILS } from '@/utils/upselling'

const DEFAULT_UPSELL_DETAIL: RouteUpSellingDetail = {
  feature: '',
  title: 'Unlock Premium Features!',
  detailsList: ['Upgrade to access this feature and more!'],
  additionalDetails: 'This feature is available with the <i>Premium Plan.</i>',
}

export type UpsellAudience = {
  isAccountOwner: boolean
  isByxbiOrg: boolean
  upgrade: () => void
}

/**
 * An upsell detail as this person should see it: owners get an upgrade button, everyone else is
 * told to ask their owner, and distributors' customers are sent to the distributor.
 */
export const resolveUpsellDetails = (
  base: RouteUpSellingDetail,
  { isAccountOwner, isByxbiOrg, upgrade }: UpsellAudience
): RouteUpSellingDetail => {
  let details: RouteUpSellingDetail = {
    ...base,
    detailsList: base.detailsList ? [...base.detailsList] : base.detailsList,
    routeName: base.routeName ? [...base.routeName] : base.routeName,
    redirectTo: base.redirectTo ? [...base.redirectTo] : base.redirectTo,
    primaryCta: {
      text: 'Upgrade Now',
      action: upgrade,
    }
  }

  /* Override for non-account owners */
  if (!isAccountOwner) {
    details.additionalDetails = NON_OWNER_UPSELLING_MESSAGE
    // Non-owners should not see the upgrade button as they cannot upgrade themselves
    details.primaryCta = undefined
  }

  /* Overrides for specific countries or distributors */
  if (isByxbiOrg) {
    details = {
      ...details,
      ...DETAILS_OVERRIDES.byxbiOrg,
    }
  }

  return details
}

type Options = {
  resolveDetail?: () => RouteUpSellingDetail | undefined
}

export const useUpselling = ({
  resolveDetail
}: Options = {}) => {
  const store = useStore()
  const router = useRouter()

  /* Currently used to skip showing the unsaved form dialog since we have a use case where user is redirected to atten */
  const isRouteAccessible = computed(() => {
    const matchedRouteDetail = ROUTE_UPSELLING_DETAILS.find(route => route?.routeName?.includes(router.currentRoute.value.name as string))

    return !matchedRouteDetail || store.state.features.includes(matchedRouteDetail?.feature)
  })

  const infoBannerDetails = computed<RouteUpSellingDetail>(() => resolveUpsellDetails(resolveDetail?.() || DEFAULT_UPSELL_DETAIL, {
    isAccountOwner: store.getters.isAccountOwner,
    isByxbiOrg: store.getters.isByxbiOrg,
    upgrade: () => router.push({ name: 'billing-subscription' }),
  }))

  return {
    isRouteAccessible,
    infoBannerDetails
  }
}